import { c as _c } from "react/compiler-runtime";
import { OptimizedImage } from '@/components/images/optimized-image';
import { SettledSeal } from '@/components/market/settled-seal';
import { Badge } from '@/components/ui/app-badge';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// Leaf module for the shared outcome row/card sub-components. Lives apart from
// `other-outcomes-table` so `other-outcome-card` can use these without the
// table↔card import cycle (table → card → these parts).

interface OutcomeThumbProps {
  marketPubkey: string;
  index: number;
  label: string;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** Tailwind size class, e.g. `size-8`. */
  size: string;
  showSettledBadge?: boolean;
  rankLabel?: string | undefined;
}

/** Outcome thumbnail (NSFW-aware, hue-tinted) shared by the desktop row + mobile card. */
export function OutcomeThumb(t0) {
  const $ = _c(22);
  const {
    marketPubkey,
    index,
    label,
    imageUrl,
    imageNsfw,
    size,
    showSettledBadge: t1,
    rankLabel
  } = t0;
  const showSettledBadge = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== index || $[1] !== label) {
    t2 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const colors = useOutcomeColor(t2);
  let t3;
  if ($[3] !== colors.cta || $[4] !== size) {
    t3 = cn("relative flex shrink-0 items-center justify-center rounded-panel-inset", size, colors.cta);
    $[3] = colors.cta;
    $[4] = size;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = imageUrl ?? null;
  let t5;
  if ($[6] !== colors.hue || $[7] !== imageNsfw || $[8] !== index || $[9] !== marketPubkey || $[10] !== t4) {
    t5 = {
      kind: "outcome",
      marketPubkey,
      outcomeIndex: index,
      hue: colors.hue,
      imageUrl: t4,
      nsfw: imageNsfw
    };
    $[6] = colors.hue;
    $[7] = imageNsfw;
    $[8] = index;
    $[9] = marketPubkey;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== size || $[13] !== t5) {
    t6 = <OptimizedImage variant="thumbnail" entity={t5} alt="" size={size} rounded="rounded-panel-inset" preview={false} loadReveal="none" className="size-full object-cover" />;
    $[12] = size;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== rankLabel || $[16] !== showSettledBadge) {
    t7 = showSettledBadge ? <SettledSeal className="pointer-events-none absolute -bottom-1.5 -right-1.5 z-10 size-5" /> : rankLabel === undefined ? null : <Badge variant="secondary" className="absolute -bottom-1.5 -right-1.5 z-10">{rankLabel}</Badge>;
    $[15] = rankLabel;
    $[16] = showSettledBadge;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t3 || $[19] !== t6 || $[20] !== t7) {
    t8 = <span className={t3}>{t6}{t7}</span>;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}